import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    AlertBannerType,
    BadgeColor,
    BadgeSize,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExBadge,
    ExButton,
    ExLink,
    LinkSize,
} from '@boomi/exosphere';
import {
    type CreateIdentityProvider,
    type IdentityProviderDetail,
    deleteIdentityProvider,
    fetchIdentityProviderById,
    updateIdentityProvider,
} from './identityProviders';
import IdentityProviderFormFields, {
    type FormValues,
    EMPTY_FORM_VALUES,
} from './IdentityProviderFormFields';
import styles from '../shared/Form.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

const valuesFromDetail = (data: IdentityProviderDetail): FormValues => ({
    ...EMPTY_FORM_VALUES,
    name: data.name,
    allowedAudience: data.allowedAudience,
    ...(data.type === 'OAUTH'
        ? {
              clientId: data.clientId,
              clientSecret: data.clientSecret,
              wellKnownUrl: data.wellKnownUrl,
          }
        : { entityId: data.entityId, metadata: data.metadata }),
});

const IdentityProviderEdit = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [provider, setProvider] = useState<IdentityProviderDetail | null>(null);
    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const [deleteState, setDeleteState] = useState<'idle' | 'confirming'>('idle');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!id) return;
        fetchIdentityProviderById(id)
            .then((data) => {
                setProvider(data);
                setValues(valuesFromDetail(data));
            })
            .catch(() => setLoadError('Failed to load identity provider.'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!provider || !id) return;
        setSubmitError(null);
        setSubmitting(true);

        const payload: CreateIdentityProvider =
            provider.type === 'OAUTH'
                ? {
                      type: 'OAUTH',
                      name: values.name,
                      allowedAudience: values.allowedAudience,
                      clientId: values.clientId,
                      clientSecret: values.clientSecret,
                      wellKnownUrl: values.wellKnownUrl,
                  }
                : {
                      type: 'SAML',
                      name: values.name,
                      allowedAudience: values.allowedAudience,
                      entityId: values.entityId,
                      metadata: values.metadata,
                  };

        try {
            await updateIdentityProvider(id, payload);
            navigate('/identity-providers');
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <ExLink onClick={() => navigate('/identity-providers')} leadingIcon={'Left arrow'}>
                    Identity Providers
                </ExLink>
                <div className={styles.headingRow}>
                    <h2 className={`${styles.heading} ex-text-h2`}>Edit Identity Provider</h2>
                    {!loading && provider && deleteState === 'idle' && (
                        <ExButton
                            type={ButtonType.SECONDARY}
                            flavor={ButtonFlavor.RISKY}
                            onClick={() => setDeleteState('confirming')}
                        >
                            Delete
                        </ExButton>
                    )}
                </div>
                {!loading && provider && deleteState === 'confirming' && (
                    <div className={styles.deleteConfirmRow}>
                        <span className={styles.deleteConfirmText}>Delete this provider?</span>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={() => setDeleteState('idle')}
                        >
                            No, keep
                        </button>
                        <ExButton
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.RISKY}
                            disabled={isDeleting}
                            onClick={async () => {
                                if (!id) return;
                                setIsDeleting(true);
                                try {
                                    await deleteIdentityProvider(id);
                                    navigate('/identity-providers');
                                } catch (err) {
                                    setIsDeleting(false);
                                    setDeleteState('idle');
                                    setSubmitError(
                                        err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE,
                                    );
                                }
                            }}
                        >
                            {isDeleting ? 'Deleting…' : 'Yes, delete'}
                        </ExButton>
                    </div>
                )}
            </div>

            {loading && <p className={styles.hint}>Loading…</p>}
            {loadError && (
                <ExAlertBanner type={AlertBannerType.ERROR} open>
                    {loadError}
                </ExAlertBanner>
            )}

            {!loading && provider && (
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className="ex-mb-medium">
                        <ExBadge
                            color={provider.type === 'OAUTH' ? BadgeColor.BLUE : BadgeColor.GREEN}
                            size={BadgeSize.EXTRASMALL}
                        >
                            {provider.type}
                        </ExBadge>
                    </div>

                    <IdentityProviderFormFields
                        type={provider.type}
                        values={values}
                        onChange={handleChange}
                    />

                    {submitError && (
                        <ExAlertBanner
                            className={styles.saveError}
                            type={AlertBannerType.ERROR}
                            open
                            onClose={() => setSubmitError(null)}
                        >
                            {submitError}
                        </ExAlertBanner>
                    )}

                    <div className={styles.actions}>
                        <ExLink
                            size={LinkSize.XSMALL}
                            onClick={() => navigate('/identity-providers')}
                        >
                            Cancel
                        </ExLink>
                        <ExButton
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            disabled={submitting}
                            onClick={() => handleSubmit()}
                        >
                            {submitting ? 'Saving…' : 'Save changes'}
                        </ExButton>
                    </div>
                </form>
            )}
        </div>
    );
};

export default IdentityProviderEdit;
